import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Archive, Lock, Copy } from "lucide-react";
import { SearchSelect } from "@/components/shared/SearchSelect";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MANDATORY = ["Passport", "CV / Resume"];
const empty = {
  title: "", department: "", location: "", employment_type: "Full-time", level: "Mid",
  salary_range: "", summary: "", responsibilities: "", requirements: "", allow_international: true, status: "published",
  required_documents: [...MANDATORY], custom_questions: [], sal_from: "", sal_to: "",
};
const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm text-[#F1F5F9] focus:border-gold focus:outline-none";
const SALARY_STEPS = Array.from({ length: 58 }, (_, i) => 1500 + i * 250);
const sgd = (n) => Number(n).toLocaleString("en-SG");
const parseSalary = (s = "") => {
  const nums = (s.match(/\d[\d,]*/g) || []).map((x) => Number(x.replace(/,/g, "")));
  return { sal_from: nums[0] ? String(nums[0]) : "", sal_to: nums[1] ? String(nums[1]) : "" };
};
const lbl = "mb-1 block font-sans text-xs text-[#94A3B8]";

const CheckList = ({ items, selected, onToggle, locked = [], testid }) => (
  <div className="grid gap-1.5 rounded-md border border-white/10 bg-[#0A0D14] p-3 sm:grid-cols-2" data-testid={testid}>
    {items.map((it) => {
      const isLocked = locked.includes(it.value);
      return (
        <label key={it.value} className={`flex items-start gap-2 font-sans text-xs ${isLocked ? "text-gold" : "text-[#CBD5E1]"}`}>
          <input type="checkbox" checked={isLocked || selected.includes(it.value)} disabled={isLocked} onChange={() => onToggle(it.value)} className="mt-0.5 accent-[#C5A059]" data-testid={`${testid}-${it.value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
          <span>{it.label} {isLocked && <Lock size={10} className="inline" />}</span>
        </label>
      );
    })}
  </div>
);

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [catalog, setCatalog] = useState({ positions: [], documents: [], questions: [] });
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/job-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const departments = useMemo(() => [...new Set(catalog.positions.map((p) => p.department))], [catalog]);
  const locations = useMemo(() => {
    const opts = properties.map((p) => `${p.name}, Singapore`);
    return form.location && !opts.includes(form.location) ? [form.location, ...opts] : opts;
  }, [properties, form.location]);
  const questionOptions = useMemo(() => {
    const opts = catalog.questions.map((q) => ({ value: q.question, label: q.question }));
    const extra = form.custom_questions.filter((q) => !opts.some((o) => o.value === q)).map((q) => ({ value: q, label: q }));
    return [...opts, ...extra];
  }, [catalog, form.custom_questions]);

  const docOptions = useMemo(() => {
    const opts = catalog.documents.map((d) => ({ value: d.label, label: d.label }));
    const extra = form.required_documents.filter((d) => !opts.some((o) => o.value === d)).map((d) => ({ value: d, label: `${d} (removed option)` }));
    return [...opts, ...extra];
  }, [catalog, form.required_documents]);

  const pickTitle = (posId) => {
    const pos = catalog.positions.find((p) => p.id === posId);
    if (!pos) return;
    setForm({ ...form, title: pos.title, department: pos.department, responsibilities: pos.responsibilities.join("\n"), requirements: pos.requirements.join("\n") });
  };
  const toggle = (key) => (v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const startNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const startEdit = (job, copy = false) => {
    setForm({
      ...job,
      ...parseSalary(job.salary_range),
      status: copy ? "draft" : job.status,
      responsibilities: job.responsibilities.join("\n"),
      requirements: job.requirements.join("\n"),
      required_documents: [...new Set([...MANDATORY, ...(job.required_documents || [])])],
      custom_questions: (job.custom_questions || []).map((q) => q.question),
    });
    setEditing(copy ? null : job.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.department || !form.location) return toast.error("Select a position, department and location");
    if (!form.summary.trim()) return toast.error("Add a short summary");
    if (form.sal_from && form.sal_to && Number(form.sal_to) < Number(form.sal_from)) return toast.error("Salary 'to' must be higher than 'from'");
    const { sal_from, sal_to, id: _id, code: _code, ...rest } = form;
    const salary_range = sal_from && sal_to ? `SGD ${sgd(sal_from)} – ${sgd(sal_to)} / month` : sal_from ? `From SGD ${sgd(sal_from)} / month` : "";
    const payload = {
      ...rest,
      salary_range,
      responsibilities: form.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
      requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
      required_documents: [...new Set([...MANDATORY, ...form.required_documents])],
      custom_questions: form.custom_questions.map((q) => {
        const opt = catalog.questions.find((o) => o.question === q);
        return { id: opt?.id || `q_${q.slice(0, 12).replace(/\W+/g, "_")}`, question: q, type: "yes_no", required: true };
      }),
    };
    try {
      if (editing) await api.put(`/admin/jobs/${editing}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(editing ? "Job updated" : form.status === "draft" ? "Job saved as draft" : "Job published");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const archive = async (id) => {
    await api.delete(`/admin/jobs/${id}`);
    toast.success("Job archived");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow">Recruitment</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight">Job Posts</h1>
        </div>
        <button onClick={startNew} data-testid="new-job-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">
          <Plus size={15} /> New Job
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {jobs.map((job) => (
          <div key={job.id} data-testid={`job-admin-row-${job.id}`} className="flex items-center justify-between rounded-lg border border-white/5 bg-[#121722] p-5">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="font-serif text-lg">{job.title}</h3>
                <StatusBadge status={job.status === "published" ? "confirmed" : job.status === "archived" ? "declined" : "Draft"} />
              </div>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{job.code} · {job.department} · {job.location}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(job)} data-testid={`job-edit-${job.id}`} className="rounded-md border border-white/10 px-4 py-2 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold">Edit</button>
              <button onClick={() => startEdit(job, true)} data-testid={`job-copy-${job.id}`} title="Copy job" className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Copy size={14} /> Copy</button>
              {job.status !== "archived" && <button onClick={() => archive(job.id)} data-testid={`job-archive-${job.id}`} className="rounded-md border border-white/10 p-2 text-[#94A3B8] hover:border-rose-500 hover:text-rose-400"><Archive size={15} /></button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Job" : form.id ? `Copy of ${form.code}` : "New Job"}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Pick a position; department, responsibilities and requirements fill in from the Job Catalog and stay editable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={lbl}>Position *</label>
              <SearchSelect value={catalog.positions.find((p) => p.title === form.title && p.department === form.department)?.id || (form.title ? "__current" : "")} onChange={(e) => pickTitle(e.target.value)} className={cls} data-testid="job-title">
                <option value="">Select position…</option>
                {departments.map((d) => (
                  <optgroup key={d} label={d}>
                    {catalog.positions.filter((p) => p.department === d).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </optgroup>
                ))}
                {form.title && !catalog.positions.some((p) => p.title === form.title && p.department === form.department) && <option value="__current">{form.title}</option>}
              </SearchSelect>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Department</label>
                <input value={form.department} readOnly className={`${cls} opacity-80`} data-testid="job-department" />
              </div>
              <div>
                <label className={lbl}>Location *</label>
                <SearchSelect value={form.location} onChange={set("location")} className={cls} data-testid="job-location">
                  <option value="">Select property…</option>
                  {locations.map((l) => <option key={l} value={l}>{l}</option>)}
                </SearchSelect>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <SearchSelect value={form.employment_type} onChange={set("employment_type")} className={cls} data-testid="job-employment-type">
                {["Full-time", "Part-time", "Seasonal", "Contract"].map((o) => <option key={o}>{o}</option>)}
              </SearchSelect>
              <SearchSelect value={form.level} onChange={set("level")} className={cls} data-testid="job-level">
                {["Junior", "Mid", "Senior", "Lead"].map((o) => <option key={o}>{o}</option>)}
              </SearchSelect>
              <SearchSelect value={form.status} onChange={set("status")} className={cls} data-testid="job-status">
                {["published", "draft", "archived"].map((o) => <option key={o}>{o}</option>)}
              </SearchSelect>
            </div>
            <label className={lbl}>Salary range (SGD / month)</label>
            <div className="grid grid-cols-2 gap-3">
              <SearchSelect value={form.sal_from} onChange={(e) => setForm((f) => ({ ...f, sal_from: e.target.value, sal_to: f.sal_to && Number(f.sal_to) <= Number(e.target.value) ? "" : f.sal_to }))} className={cls} data-testid="job-salary-from">
                <option value="">From…</option>
                {SALARY_STEPS.map((n) => <option key={n} value={n}>SGD {sgd(n)}</option>)}
              </SearchSelect>
              <SearchSelect value={form.sal_to} onChange={set("sal_to")} className={cls} data-testid="job-salary-to">
                <option value="">To…</option>
                {SALARY_STEPS.filter((n) => !form.sal_from || n > Number(form.sal_from)).map((n) => <option key={n} value={n}>SGD {sgd(n)}</option>)}
              </SearchSelect>
            </div>
            <textarea placeholder="Summary *" value={form.summary} onChange={set("summary")} rows={2} className={cls} data-testid="job-summary" />
            <label className={lbl}>Responsibilities (one per line)</label>
            <textarea value={form.responsibilities} onChange={set("responsibilities")} rows={5} className={cls} data-testid="job-responsibilities" />
            <label className={lbl}>Requirements (one per line)</label>
            <textarea value={form.requirements} onChange={set("requirements")} rows={4} className={cls} data-testid="job-requirements" />
            <label className={lbl}>Required documents (Passport and CV are always required)</label>
            <CheckList items={docOptions} selected={form.required_documents} onToggle={toggle("required_documents")} locked={MANDATORY} testid="job-docs" />
            <label className={lbl}>Screening questions (Yes / No)</label>
            <CheckList items={questionOptions} selected={form.custom_questions} onToggle={toggle("custom_questions")} testid="job-questions" />
            <button onClick={save} data-testid="job-save" className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">Save Job</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
